import { AxiosResponse } from 'axios';

import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

type UserParams = {
  candidateId: string;
};

type DeleteProjectType = UserParams & {
  id: string;
};

export type NewProjectType = UserParams & {
  id?: string;
  title: string;
  description: string;
  link: string;
  year: string;
  skills: string[];
};

type CommonType = {
  updatedAt: string;
  id: string;
  createdAt: string;
};

type Skill = CommonType & {
  name: string;
};

type SkillsType = CommonType & {
  updatedAt: string;
  id: string;
  createdAt: string;
  skill: Skill;
};

export type ProjectType = {
  id: string;
  createdAt: string;
  description: string;
  link: string;
  skills: SkillsType[];
  title: string;
  updatedAt: string;
  year: string;
};

type ProjectResponseType = {
  projects: ProjectType[];
};

const getAllProjects = async ({ candidateId }: UserParams): Promise<AxiosResponse<ProjectResponseType>> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*, project_skills(skill_id, skills(*))')
    .eq('profile_id', candidateId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const projects = (data || []).map((row: any) => ({
    id: row.id,
    title: row.title,
    description: row.description,
    link: row.link,
    year: row.year,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    skills: (row.project_skills || []).map((ps: any) => ({
      id: ps.skill_id,
      createdAt: ps.skills?.created_at || '',
      updatedAt: ps.skills?.updated_at || '',
      skill: {
        id: ps.skill_id,
        name: ps.skills?.name || '',
        createdAt: ps.skills?.created_at || '',
        updatedAt: ps.skills?.updated_at || '',
      },
    })),
  }));

  return asResponse({ projects }) as unknown as AxiosResponse<ProjectResponseType>;
};

const createNewProject = async ({ candidateId, skills, ...project }: NewProjectType): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      profile_id: candidateId,
      title: project.title,
      description: project.description,
      link: project.link,
      year: project.year,
    })
    .select()
    .single();

  if (error) throw error;

  // Insert project_skills
  if (skills && skills.length > 0) {
    const rows = skills.map((skillId) => ({
      project_id: data.id,
      skill_id: skillId,
    }));

    const { error: skillsError } = await supabase
      .from('project_skills')
      .insert(rows);

    if (skillsError) throw skillsError;
  }

  return asResponse(data);
};

const updateProject = async ({ candidateId, id, skills, ...project }: NewProjectType): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('projects')
    .update({
      title: project.title,
      description: project.description,
      link: project.link,
      year: project.year,
    })
    .eq('id', id)
    .eq('profile_id', candidateId)
    .select()
    .single();

  if (error) throw error;

  // Replace project_skills: delete then insert
  const { error: deleteError } = await supabase
    .from('project_skills')
    .delete()
    .eq('project_id', id);

  if (deleteError) throw deleteError;

  if (skills && skills.length > 0) {
    const rows = skills.map((skillId) => ({
      project_id: id,
      skill_id: skillId,
    }));

    const { error: skillsError } = await supabase
      .from('project_skills')
      .insert(rows);

    if (skillsError) throw skillsError;
  }

  return asResponse(data);
};

const deleteProject = async ({ candidateId, id }: DeleteProjectType): Promise<AxiosResponse<any>> => {
  // Delete project_skills first
  await supabase
    .from('project_skills')
    .delete()
    .eq('project_id', id);

  const { data, error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id)
    .eq('profile_id', candidateId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

export default {
  getAllProjects,
  createNewProject,
  updateProject,
  deleteProject,
};
